/**
 * @fileoverview Cliente HTTP centralizado para los endpoints públicos
 * del backend `/api/v1/...`. Usa fetch nativo (sin axios). Valida cada
 * respuesta con Zod y arroja errores tipados.
 *
 * Variables de entorno:
 *   - REACT_APP_BACKEND_URL: URL pública del backend (Emergent ingress).
 *
 * Si más adelante el frontend y backend comparten dominio, basta
 * dejar `REACT_APP_BACKEND_URL` vacío para que las rutas resuelvan
 * relativas al origen actual.
 */

import {
    BrechasResponse,
    ParticipacionCompleteResponse,
    ParticipacionResumeResponse,
    ParticipacionStartResponse,
    RespuestaAbiertaResponse,
    TokenValidateResponse,
    VotoCreateResponse,
    VotosMeResponse,
} from "./schemas";

const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Si REACT_APP_BACKEND_URL viene con barra final, la quitamos.
const BACKEND = RAW_BACKEND.replace(/\/$/, "");
const API_PREFIX = "/api/v1";
export const API_BASE = `${BACKEND}${API_PREFIX}`;

/** Errores de API normalizados. */
export class ApiError extends Error {
    /**
     * @param {string} code
     * @param {number} status
     * @param {string} [mensaje]
     * @param {unknown} [detalle]
     */
    constructor(code, status, mensaje, detalle) {
        super(mensaje || code);
        this.code = code;
        this.status = status;
        this.detalle = detalle;
    }
}

/** @returns {string | null} */
function obtenerToken() {
    try {
        return sessionStorage.getItem("nl.session_token");
    } catch (e) {
        // intencional: en modo privado / cookies bloqueadas sessionStorage
        // puede lanzar; devolvemos null para que el flujo siga sin sesión.
        console.error("[api] sessionStorage no disponible:", e);
        return null;
    }
}

/**
 * @template T
 * @param {string} path
 * @param {{ method?: string, body?: unknown, auth?: boolean, schema?: import("zod").ZodType<T> }} opts
 * @returns {Promise<T>}
 */
async function request(path, opts = {}) {
    const { method = "GET", body, auth = false, schema } = opts;
    /** @type {Record<string,string>} */
    const headers = { "Content-Type": "application/json" };
    if (auth) {
        const token = obtenerToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    }

    let response;
    try {
        response = await fetch(`${API_BASE}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        throw new ApiError("network_error", 0, "Sin conexión", e);
    }

    // Leer el body de la respuesta de forma defensiva. En algunos
    // entornos (Cloudflare + ingress + automation hooks) el stream
    // del body puede haber sido consumido cuando llegamos aquí, lo
    // que provoca `TypeError: body stream already read`. Usamos
    // response.clone() y un try-catch para evitar que esa excepción
    // escape como TypeError sin envolverse en ApiError.
    let data = null;
    try {
        const text = await response.clone().text();
        if (text) {
            try {
                data = JSON.parse(text);
            } catch (e) {
                // intencional: algunos endpoints o gateways pueden devolver
                // texto plano / HTML de error; seguimos con data=null.
                console.error(
                    `[api] respuesta no-JSON en ${method} ${path} (status ${response.status}):`,
                    e,
                );
            }
        }
    } catch (e) {
        // El stream del body fue consumido (race con Cloudflare/ingress);
        // dejamos data=null para que el caller maneje el error sin crashear.
        console.error(`[api] body inaccesible en ${method} ${path}:`, e);
    }

    if (!response.ok) {
        const detail = data && typeof data === "object" ? data.detail : undefined;
        // FastAPI puede devolver `detail` como string, como lista (errores
        // de validación Pydantic) o como objeto cuando exponemos códigos
        // ricos del backend (p.ej. token_expirado en HTTP 410 trae
        // `{code, mensaje, expires_at}`). Soportamos los tres.
        let code;
        let mensaje;
        if (typeof detail === "string") {
            code = detail;
        } else if (Array.isArray(detail)) {
            code = "validation_error";
        } else if (detail && typeof detail === "object") {
            code = typeof detail.code === "string" ? detail.code : `http_${response.status}`;
            mensaje = typeof detail.mensaje === "string" ? detail.mensaje : undefined;
        } else {
            code = `http_${response.status}`;
        }
        throw new ApiError(code, response.status, mensaje, data);
    }

    if (schema) {
        try {
            return schema.parse(data);
        } catch (e) {
            throw new ApiError("invalid_response", response.status, "Respuesta inválida", e);
        }
    }
    return /** @type {T} */ (data);
}

/* ===================== Endpoints públicos ===================== */

/**
 * @param {string} codigo
 */
export async function validarToken(codigo) {
    return request("/tokens/validate", {
        method: "POST",
        body: { codigo },
        schema: TokenValidateResponse,
    });
}

/**
 * @param {string} codigo
 * @param {import("./schemas").Datos} datos
 */
export async function iniciarParticipacion(codigo, datos) {
    return request("/participaciones/start", {
        method: "POST",
        body: { codigo, datos },
        schema: ParticipacionStartResponse,
    });
}

/**
 * @param {string} codigo
 */
export async function reanudarParticipacion(codigo) {
    return request("/participaciones/resume", {
        method: "POST",
        body: { codigo },
        schema: ParticipacionResumeResponse,
    });
}

export async function obtenerBrechas() {
    return request("/brechas", {
        method: "GET",
        auth: true,
        schema: BrechasResponse,
    });
}

/**
 * @param {string} statement_id
 * @param {import("./schemas").ValorVotoTipo} valor
 */
export async function registrarVoto(statement_id, valor) {
    return request("/votos", {
        method: "POST",
        body: { statement_id, valor },
        auth: true,
        schema: VotoCreateResponse,
    });
}

export async function obtenerVotosPropios() {
    return request("/participaciones/me/votos", {
        method: "GET",
        auth: true,
        schema: VotosMeResponse,
    });
}

/**
 * @param {string} texto
 */
export async function registrarRespuestaAbierta(texto) {
    return request("/respuestas_abiertas", {
        method: "POST",
        body: { texto },
        auth: true,
        schema: RespuestaAbiertaResponse,
    });
}

export async function completarParticipacion() {
    return request("/participaciones/complete", {
        method: "POST",
        auth: true,
        schema: ParticipacionCompleteResponse,
    });
}
